import { createTranslatedOptions } from '@examdojo/core/i18n';
import {
  STEM_DIFFICULTIES,
  STEM_DIFFICULTY_CATEGORY_TO_LABEL_KEY,
  StemDifficultyCategory,
} from '@examdojo/models/question';
import {
  DERIVATIVE_QUESTION_ITEMS_VIEW_STATUS_TO_LABEL_KEY,
  DerivativeQuestionItemsViewStatus,
} from '@examdojo/question';
import { TranslocoService } from '@jsverse/transloco';
import { combineLatest, map, Observable, of, switchMap } from 'rxjs';
import { QuestionBankQuery } from '../app/question-bank.query';

export function createDifficultyOptions$() {
  return createTranslatedOptions(STEM_DIFFICULTIES, STEM_DIFFICULTY_CATEGORY_TO_LABEL_KEY);
}

export function createDifficultyLabelsMap$(difficultyOptions$: Observable<Array<{ value: StemDifficultyCategory; label: string }>>) {
  return difficultyOptions$.pipe(
    map((options) =>
      options.reduce<Partial<Record<StemDifficultyCategory, string>>>((record, option) => {
        record[option.value] = option.label;
        return record;
      }, {}),
    ),
  );
}

export function createTypeOptions$() {
  return createTranslatedOptions(
    Object.values(DerivativeQuestionItemsViewStatus),
    DERIVATIVE_QUESTION_ITEMS_VIEW_STATUS_TO_LABEL_KEY,
  );
}

export function createTypeLabelsMap$(typeOptions$: Observable<Array<{ value: DerivativeQuestionItemsViewStatus; label: string }>>) {
  return typeOptions$.pipe(
    map((options) =>
      options.reduce<Partial<Record<DerivativeQuestionItemsViewStatus, string>>>((record, option) => {
        record[option.value] = option.label;
        return record;
      }, {}),
    ),
  );
}

export function createSelectedDifficultyLabel$(
  query: QuestionBankQuery,
  labelsMap$: Observable<Partial<Record<StemDifficultyCategory, string>>>,
) {
  return combineLatest([
    query.filtersFormValue$.pipe(map((f) => f.difficulties)),
    labelsMap$,
  ]).pipe(map(([[value], labelsMap]) => labelsMap[value] ?? ''));
}

export function createSelectedTypeLabel$(
  query: QuestionBankQuery,
  labelsMap$: Observable<Partial<Record<DerivativeQuestionItemsViewStatus, string>>>,
) {
  return combineLatest([
    query.filtersFormValue$.pipe(map((f) => f.types)),
    labelsMap$,
  ]).pipe(map(([[value], labelsMap]) => labelsMap[value] ?? ''));
}

export function createSelectedTagLabel$(query: QuestionBankQuery) {
  return combineLatest([
    query.filtersFormValue$.pipe(map((f) => f.tags)),
    query.selectProp('tags'),
  ]).pipe(
    map(([selectedTagIds, tags]) => {
      if (selectedTagIds.length !== 1) {
        return '';
      }
      return tags.find((t) => t.id === selectedTagIds[0])?.name ?? '';
    }),
  );
}

export function createSelectedMaxMarksLabel$(query: QuestionBankQuery, translocoService: TranslocoService) {
  return combineLatest([
    query.filtersFormValue$,
    query.selectProp('marksRange'),
  ]).pipe(
    switchMap(([filters, marksRange]) =>
      filters.maxPoints < marksRange.max || filters.minPoints > marksRange.min
        ? translocoService.selectTranslate('examdojo.max_marks_range', { min: filters.minPoints, max: filters.maxPoints })
        : of(''),
    ),
  );
}

export function createTopicsFilterActiveCount$(query: QuestionBankQuery) {
  return query.selectProp('filters').pipe(map((filters) => filters.topicIds?.length ?? 0));
}
